import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertCircle, PackageCheck, Wrench, Package } from "lucide-react";

const LINE_STATUS_STYLE = {
  pending:  "bg-slate-100 text-slate-700",
  partial:  "bg-amber-100 text-amber-800",
  received: "bg-emerald-100 text-emerald-800",
};

function LineStatusPill({ status }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${LINE_STATUS_STYLE[status] || LINE_STATUS_STYLE.pending}`}>
      {status === "received" ? "completa" : status === "partial" ? "parcial" : "pendiente"}
    </span>
  );
}

export function ReceptionDialog({ open, onOpenChange, po, parts, onSuccess }) {
  const [qtyMap, setQtyMap] = useState({});         // line_id -> qty_to_receive
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [linkingLineId, setLinkingLineId] = useState(null); // line_id currently linking a part
  const [partSelect, setPartSelect] = useState({});   // line_id -> selected part_id

  useEffect(() => {
    if (open) {
      setQtyMap({});
      setNotes("");
      setLinkingLineId(null);
      setPartSelect({});
    }
  }, [open, po?.id]);

  const lines = po?.lines || [];
  const receivableLines = lines.filter((l) => Number(l.remaining_qty || 0) > 0);
  const unlinkedInvLines = lines.filter(
    (l) => l.line_type === "inventariable" && !l.part_id && Number(l.remaining_qty || 0) > 0,
  );
  const hasUnlinked = unlinkedInvLines.length > 0;

  const totalToReceive = Object.values(qtyMap).reduce((acc, v) => acc + (Number(v) || 0), 0);

  function updateQty(lineId, val, remaining) {
    let v = val === "" ? "" : Number(val);
    if (Number.isNaN(v)) v = "";
    if (v !== "" && v < 0) v = 0;
    if (v !== "" && v > remaining) v = remaining;
    setQtyMap((m) => ({ ...m, [lineId]: v }));
  }

  function fillAllRemaining() {
    const next = {};
    for (const l of receivableLines) next[l.id] = Number(l.remaining_qty || 0);
    setQtyMap(next);
  }

  function clearAll() {
    setQtyMap({});
  }

  async function linkPart(lineId) {
    const partId = partSelect[lineId];
    if (!partId) { toast.error("Selecciona una refacción del catálogo"); return; }
    setLinkingLineId(lineId);
    try {
      const { data } = await api.patch(
        `/purchase-orders/${po.id}/lines/${lineId}/part`,
        { part_id: partId },
      );
      toast.success("Refacción vinculada");
      onSuccess?.(data);   // parent reloads
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLinkingLineId(null);
    }
  }

  async function submit() {
    const entries = Object.entries(qtyMap)
      .filter(([, v]) => Number(v || 0) > 0)
      .map(([line_id, v]) => ({ line_id, qty_to_receive: Number(v) }));
    if (entries.length === 0) {
      toast.error("Captura al menos una cantidad a recibir");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(
        `/purchase-orders/${po.id}/receive`,
        { lines: entries, notes: notes.trim() || null },
      );
      const parts_moved = data.stock_movements_created;
      toast.success(
        `Recepción registrada · ${data.lines_touched} línea${data.lines_touched === 1 ? " avanzada" : "s avanzadas"}, ${parts_moved} movimiento${parts_moved === 1 ? "" : "s"} de kardex. Estatus: ${data.po_status}`,
      );
      onOpenChange(false);
      onSuccess?.(data.purchase_order);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="po-reception-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PackageCheck className="h-5 w-5" /> Registrar recepción
          </DialogTitle>
          <DialogDescription>
            Captura las cantidades que vas a recibir en <b>esta</b> recepción por partida.
            Las líneas inventariables generan entrada real al kardex; las de servicio solo actualizan avance.
          </DialogDescription>
        </DialogHeader>

        {hasUnlinked && (
          <Alert className="bg-amber-50 border-amber-200" data-testid="po-reception-unlinked-alert">
            <AlertCircle className="h-4 w-4 text-amber-700" />
            <AlertDescription className="text-sm text-amber-900">
              <b>{unlinkedInvLines.length}</b> partida{unlinkedInvLines.length === 1 ? "" : "s"} inventariable{unlinkedInvLines.length === 1 ? "" : "s"} no
              tiene{unlinkedInvLines.length === 1 ? "" : "n"} refacción vinculada del catálogo.
              Vincula la refacción antes de recibirla (los inputs de esas líneas están deshabilitados).
            </AlertDescription>
          </Alert>
        )}

        <div className="border border-border rounded-md overflow-x-auto">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead className="text-xs">Partida</TableHead>
                <TableHead className="text-xs text-right">Pedido</TableHead>
                <TableHead className="text-xs text-right">Ya recibido</TableHead>
                <TableHead className="text-xs text-right">Pendiente</TableHead>
                <TableHead className="text-xs">Línea</TableHead>
                <TableHead className="text-xs w-40">A recibir ahora</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lines.map((l) => {
                const remaining = Number(l.remaining_qty || 0);
                const isInv = l.line_type === "inventariable";
                const needsPart = isInv && !l.part_id && remaining > 0;
                const disabled = remaining === 0 || needsPart;
                return (
                  <TableRow key={l.id} data-testid={`po-rcp-line-${l.id}`}>
                    <TableCell className="text-sm max-w-md">
                      <div className="flex items-center gap-2">
                        {isInv ? <Package className="h-3.5 w-3.5 text-slate-500 shrink-0" /> : <Wrench className="h-3.5 w-3.5 text-slate-500 shrink-0" />}
                        <span className="font-medium">{l.description}</span>
                      </div>
                      {needsPart && (
                        <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid={`po-rcp-line-${l.id}-link-row`}>
                          <Select
                            value={partSelect[l.id] || ""}
                            onValueChange={(v) => setPartSelect((m) => ({ ...m, [l.id]: v }))}
                          >
                            <SelectTrigger className="h-8 min-w-[220px]" data-testid={`po-rcp-line-${l.id}-part-select`}>
                              <SelectValue placeholder="Vincular refacción del catálogo…" />
                            </SelectTrigger>
                            <SelectContent>
                              {(parts || []).map((p) => (
                                <SelectItem key={p.id} value={p.id}>{p.sku} — {p.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!partSelect[l.id] || linkingLineId === l.id}
                            onClick={() => linkPart(l.id)}
                            data-testid={`po-rcp-line-${l.id}-link-btn`}
                          >
                            {linkingLineId === l.id ? "Vinculando…" : "Vincular"}
                          </Button>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right mono text-sm">{Number(l.quantity).toFixed(2)}</TableCell>
                    <TableCell className="text-right mono text-sm">{Number(l.qty_received || 0).toFixed(2)}</TableCell>
                    <TableCell className="text-right mono text-sm font-medium">{remaining.toFixed(2)}</TableCell>
                    <TableCell><LineStatusPill status={l.line_status || "pending"} /></TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        max={remaining}
                        value={qtyMap[l.id] ?? ""}
                        onChange={(e) => updateQty(l.id, e.target.value, remaining)}
                        disabled={disabled || saving}
                        placeholder={disabled ? "—" : "0"}
                        className="h-8"
                        data-testid={`po-rcp-line-${l.id}-qty`}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <Button size="sm" variant="outline" onClick={fillAllRemaining} disabled={saving || hasUnlinked} data-testid="po-rcp-fill-all">
            Recibir todo lo pendiente
          </Button>
          <Button size="sm" variant="ghost" onClick={clearAll} disabled={saving} data-testid="po-rcp-clear">
            Vaciar
          </Button>
          <div className="ml-auto text-xs text-muted-foreground">
            Total a recibir en esta operación:{" "}
            <b className="text-slate-800 mono" data-testid="po-rcp-total">{totalToReceive.toFixed(2)}</b> uds/servicios
          </div>
        </div>

        <div className="space-y-2">
          <Label>Notas de esta recepción (opcional)</Label>
          <Textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Ej. Se recibe con factura F-1234, faltó una caja de balatas."
            data-testid="po-rcp-notes"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button
            className="btn-tenant"
            onClick={submit}
            disabled={saving || totalToReceive === 0}
            data-testid="po-rcp-submit"
          >
            {saving ? "Guardando…" : "Confirmar recepción"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
